// The footer: graphite rising to its deepest, a large faint mark in the corner,
// then four columns and the legal line.
//
// Everything Shared/Footer offered is here: the logo and its sentence, the
// social icons, the module names (plain text on purpose: they name what is
// inside the product, they are not pages), the section links and login, email
// and phone, the sign-up button, and the copyright. As there, each link goes
// to something that exists: «آراء العملاء» only while that section is on the
// page, «السعر» only while a plan is on sale, and an empty email or phone is not shown.
//
// The legal links appear once their page has text: the owner supplies that wording,
// and a link to an empty page says less than no link.

import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Mail, Music2, Phone, Twitter, Youtube } from "lucide-react";
import RayesMark from "../../brand/RayesMark";
import { HOW, signupHref } from "../Home/content";
import CtaLink from "./CtaLink";
import { telHref } from "./data";
import { FOOTER } from "./copy";
import "./LandingFooter.css";

const SOCIALS = [
    { key: "facebook", Icon: Facebook, label: "فيسبوك" },
    { key: "instagram", Icon: Instagram, label: "إنستغرام" },
    { key: "linkedin", Icon: Linkedin, label: "لينكدإن" },
    { key: "x", Icon: Twitter, label: "X (تويتر)" },
    { key: "youtube", Icon: Youtube, label: "يوتيوب" },
    { key: "tiktok", Icon: Music2, label: "تيك توك" },
];

const isUrl = (v) => /^https?:\/\/\S+$/i.test(String(v || "").trim());

const platformLinks = ({ hasPricing, hasTestimonials }) => [
    { label: "يوم الريّس", href: "#day" },
    ...(hasPricing ? [{ label: "السعر", href: "#pricing" }] : []),
    { label: "إزاي تبدأ", href: "#how" },
    { label: "أسئلة", href: "#faq" },
    ...(hasTestimonials ? [{ label: "آراء العملاء", href: "#testimonials" }] : []),
    { label: "تواصل معنا", href: "#contact" },
];

export default function LandingFooter({
    cta = null,
    tagline = "",
    email = "",
    phone = "",
    social = {},
    hasPricing = true,
    hasTestimonials = false,
    legal = {},
}) {
    const socials = SOCIALS.filter((s) => isUrl(social?.[s.key]));
    const year = new Date().getFullYear();

    return (
        <footer className="ld-footer ld-section--rise">
            <div className="ld-decor" aria-hidden="true">
                <div className="ld-gridlines" style={{ "--ld-grid-at": "20% 30%" }} />
                <div className="ld-grain" />
                <div className="ld-footer__mark"><RayesMark size={48} title="" /></div>
            </div>

            <div className="ld-container">
                <div className="ld-footer__cols" data-ld="group" data-ld-early="">
                    <div className="ld-footer__brand">
                        <a href="#top" className="ld-footer__logo" aria-label="الريّس — أول الصفحة">
                            <RayesMark size={48} title="" />
                            <span aria-hidden="true">الريّس</span>
                        </a>
                        {tagline ? <p className="ld-footer__tagline">{tagline}</p> : null}
                        <p className="ld-footer__descriptor ld-small">{FOOTER.descriptor}</p>
                        {socials.length ? (
                            <div className="ld-footer__socials">
                                {socials.map(({ key, Icon, label }) => (
                                    <a
                                        key={key}
                                        href={social[key].trim()}
                                        aria-label={label}
                                        title={label}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ld-footer__social"
                                    >
                                        <span className="ld-disc ld-disc--sm"><Icon aria-hidden="true" /></span>
                                    </a>
                                ))}
                            </div>
                        ) : null}
                    </div>

                    <div className="ld-footer__col">
                        <h2 className="ld-footer__heading">{FOOTER.modulesTitle}</h2>
                        <ul className="ld-footer__list">
                            {FOOTER.modules.map((m) => <li key={m} className="ld-footer__item">{m}</li>)}
                        </ul>
                    </div>

                    <nav className="ld-footer__col" aria-label={FOOTER.platformTitle}>
                        <h2 className="ld-footer__heading">{FOOTER.platformTitle}</h2>
                        <ul className="ld-footer__list">
                            {platformLinks({ hasPricing, hasTestimonials }).map((l) => (
                                <li key={l.href}><a href={l.href} className="ld-footer__link">{l.label}</a></li>
                            ))}
                            <li><Link to="/auth" className="ld-footer__link">تسجيل الدخول</Link></li>
                        </ul>
                    </nav>

                    <div className="ld-footer__contact">
                        <h2 className="ld-footer__heading">{FOOTER.contactTitle}</h2>
                        {email || phone ? (
                            <ul className="ld-footer__rows">
                                {phone ? (
                                    <li className="ld-footer__row">
                                        <Phone aria-hidden="true" />
                                        <a href={telHref(phone)} dir="ltr" className="ld-footer__link">{phone}</a>
                                    </li>
                                ) : null}
                                {email ? (
                                    <li className="ld-footer__row">
                                        <Mail aria-hidden="true" />
                                        <a href={`mailto:${email}`} dir="ltr" className="ld-footer__link">{email}</a>
                                    </li>
                                ) : null}
                            </ul>
                        ) : null}
                        <CtaLink href={cta?.href || signupHref()} className="ld-btn ld-btn--primary ld-footer__cta">{cta?.text || HOW.ctaText}</CtaLink>
                    </div>
                </div>

                <div className="ld-footer__bottom" data-ld="fade" data-ld-early="" style={{ "--ld-d": ".2s" }}>
                    <p>© <span className="ld-footer__year">{year}</span> {FOOTER.rights}</p>
                    {legal.privacy || legal.terms ? (
                        <p className="ld-footer__legal">
                            {legal.privacy ? <Link to="/privacy" className="ld-footer__link">سياسة الخصوصية</Link> : null}
                            {legal.privacy && legal.terms ? " · " : null}
                            {legal.terms ? <Link to="/terms" className="ld-footer__link">الشروط والأحكام</Link> : null}
                        </p>
                    ) : null}
                </div>
            </div>
        </footer>
    );
}
